import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from "@/shared/ui/button";
import { Input } from "@/shared/ui/input";
import { Alert, AlertDescription } from "@/shared/ui/alert";
import { usePasswordResetWithToken } from '../hooks/usePasswordResetWithToken';
import type { PasswordResetWithTokenFormData } from '../types';

interface PasswordResetWithTokenFormProps {
  uidb64: string;
  token: string;
  onSuccess?: () => void;
}

export const PasswordResetWithTokenForm: React.FC<PasswordResetWithTokenFormProps> = ({
  uidb64,
  token,
  onSuccess
}) => {
  const {
    isLoading,
    isSuccess,
    successMessage,
    userEmail,
    errors: apiErrors,
    handlePasswordReset,
    clearError
  } = usePasswordResetWithToken();

  const [formData, setFormData] = useState<PasswordResetWithTokenFormData>({
    newPassword: '',
    confirmPassword: '',
  });

  const [validationErrors, setValidationErrors] = useState<{ [key: string]: string[] }>({});
  
  // Merge API errors and validation errors
  const errors = { ...validationErrors, ...apiErrors };

  // Call onSuccess callback when reset is successful
  useEffect(() => {
    if (isSuccess && onSuccess) {
      onSuccess();
    }
  }, [isSuccess, onSuccess]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear both validation and API errors for this field
    if (validationErrors[name]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
    if (apiErrors[name]) {
      clearError(name);
    }
  };

  const validateForm = (): boolean => {
    const newErrors: { [key: string]: string[] } = {};

    // Required field validation
    if (!formData.newPassword) {
      newErrors.newPassword = ['New password is required'];
    } else if (formData.newPassword.length < 8) {
      newErrors.newPassword = ['Password must be at least 8 characters'];
    }
    if (!formData.confirmPassword) {
      newErrors.confirmPassword = ['Please confirm your new password'];
    } else if (formData.newPassword !== formData.confirmPassword) {
      newErrors.confirmPassword = ['Passwords do not match'];
    }

    setValidationErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Clear any existing validation errors before validation
    setValidationErrors({});
    
    if (!validateForm()) {
      // Scroll to first error
      setTimeout(() => {
        const firstError = document.querySelector('.border-red-500');
        if (firstError) {
          firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
      return;
    }

    await handlePasswordReset(formData, uidb64, token);
  };

  // Show success message
  if (isSuccess) {
    return (
      <div className="space-y-4">
        <Alert className="bg-green-50 border-green-200">
          <AlertDescription className="text-green-800">
            <div className="flex items-start gap-3">
              <svg className="w-6 h-6 text-green-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div className="flex-1">
                <p className="font-semibold mb-1">Password Reset Successfully!</p>
                <p className="text-sm">{successMessage}</p>
                {userEmail && (
                  <p className="text-sm mt-2">Account: <span className="font-medium">{userEmail}</span></p>
                )}
              </div>
            </div>
          </AlertDescription>
        </Alert>
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-sm text-blue-900">
            <strong>Your password has been updated.</strong>
          </p>
          <p className="text-sm text-blue-800 mt-2">
            You can now log in with your new password.
          </p>
        </div>
        <div className="text-center">
          <Link to="/login">
            <Button className="w-full">
              Go to Login
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Info Banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <svg className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <div className="flex-1">
            <p className="text-sm font-semibold text-blue-900">
              Password Reset Required
            </p>
            <p className="text-sm mt-1 text-blue-800">
              Please enter a new password for your account. Make sure it's at least 8 characters long.
            </p>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        {errors.non_field_errors && (
          <Alert variant="destructive">
            <AlertDescription>
              {errors.non_field_errors.join(' ')}
            </AlertDescription>
          </Alert>
        )}

        {errors.token && (
          <Alert variant="destructive">
            <AlertDescription>
              {errors.token.join(' ')}
            </AlertDescription>
          </Alert>
        )}

        <div className="space-y-2">
          <label htmlFor="newPassword" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
            New Password <span className="text-red-500">*</span>
          </label>
          <Input
            id="newPassword"
            name="newPassword"
            type="password"
            autoComplete="new-password"
            placeholder="Minimum 8 characters"
            required
            value={formData.newPassword}
            onChange={handleInputChange}
            className={errors.newPassword ? "border-red-500 focus-visible:ring-red-500" : ""}
          />
          {errors.newPassword && (
            <p className="text-sm text-red-600 font-medium">{errors.newPassword.join(' ')}</p>
          )}
        </div>

        <div className="space-y-2">
          <label htmlFor="confirmPassword" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
            Confirm New Password <span className="text-red-500">*</span>
          </label>
          <Input
            id="confirmPassword"
            name="confirmPassword"
            type="password"
            autoComplete="new-password"
            placeholder="Confirm your new password"
            required
            value={formData.confirmPassword}
            onChange={handleInputChange}
            className={errors.confirmPassword ? "border-red-500 focus-visible:ring-red-500" : ""}
          />
          {errors.confirmPassword && (
            <p className="text-sm text-red-600 font-medium">{errors.confirmPassword.join(' ')}</p>
          )}
        </div>

        <Button 
          type="submit" 
          className="w-full"
          disabled={isLoading}
        >
          {isLoading ? "Resetting Password..." : "Reset Password"}
        </Button>

        <div className="text-center text-sm text-gray-600">
          Remember your password?{' '}
          <Link to="/login" className="text-primary-500 hover:text-primary-600 underline">
            Back to Login
          </Link>
        </div>
      </form>
    </div>
  );
};

